import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Palette, Type, WandSparkles, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type SiteAppearance,
  type SitePalette,
  appearancePrompt,
  siteStyles,
} from './site-appearance';

import { siteElementSelector } from './site-preview';

type Target = {
  key: string;
  label: string;
  value: string;
  tag: string;
  selector: string;
};
export function editableSiteElements(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text: Target[] = Array.from(doc.querySelectorAll('h1,h2,h3,p,a,button'))
    .filter((node) => node.textContent?.trim())
    .slice(0, 60)
    .map((node, index, nodes) => {
      const value = node.textContent?.trim() || '';
      const matches = nodes.filter(
        (item) =>
          item.tagName === node.tagName && item.textContent?.trim() === value
      );
      const occurrence =
        matches.length > 1
          ? ` (${matches.indexOf(node) + 1} of ${matches.length})`
          : '';
      return {
        key: `text-${index}`,
        tag: node.tagName.toLowerCase(),
        label: `${node.tagName.toLowerCase()} · ${value.slice(0, 65)}${occurrence}`,
        value,
        selector: siteElementSelector(node, doc.body),
      };
    });
  return { text };
}

export function SiteEditControls({
  html,
  disabled,
  onApply,
  onClose,
  appearance,
  onPreviewAppearance,
  palette,
}: {
  html: string;
  palette?: SitePalette;
  disabled: boolean;
  onApply: (prompt: string) => void;
  onClose: () => void;
  appearance: SiteAppearance | null;
  onPreviewAppearance: (value: SiteAppearance | null) => void;
}) {
  const elements = useMemo(() => editableSiteElements(html), [html]);
  const [tab, setTab] = useState<'text' | 'style'>('text');
  const [targetKey, setTargetKey] = useState('');
  const [value, setValue] = useState('');
  const targets = elements.text;
  const target = targets.find((item) => item.key === targetKey);
  const preserve =
    'Change only this detail. Preserve the rest of the page, all booking URLs, and the existing public business facts.';
  function apply() {
    if (!target || disabled) return;
    const instruction = `For only the ${target.tag} at CSS selector ${JSON.stringify(target.selector)} whose exact text is ${JSON.stringify(target.value)}, replace its visible text with ${JSON.stringify(value)}. ${preserve}`;
    onApply(instruction);
  }
  return (
    <section
      aria-label="Edit website details"
      className="site-details border-b bg-background/95 p-4"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <ToggleGroup
          type="single"
          value={tab}
          aria-label="Edit mode"
          onValueChange={(value) => {
            if (!value) return;
            setTab(value as typeof tab);
            setTargetKey('');
            setValue('');
          }}
          className="gap-1 rounded-lg bg-muted p-1"
        >
          {(
            [
              { key: 'text', label: 'Text', Icon: Type },
              { key: 'style', label: 'Style', Icon: Palette },
            ] as const
          ).map(({ key, label, Icon }) => (
            <ToggleGroupItem
              key={key}
              value={key}
              size="sm"
              className={
                tab === key
                  ? 'bg-background text-primary shadow-sm hover:bg-background'
                  : ''
              }
              aria-pressed={tab === key}
            >
              <Icon className="size-3.5" />
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Close detail editor"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>
      {tab === 'style' ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            ...siteStyles,
            ...(palette
              ? [
                  {
                    id: 'signature' as const,
                    name: 'Your look',
                    colors: [
                      palette.background,
                      palette.primary,
                      palette.accent,
                    ],
                  },
                ]
              : []),
          ].map((style) => (
            <Button
              variant="outline"
              key={style.name}
              type="button"
              disabled={disabled}
              aria-pressed={appearance?.style === style.id}
              className={`site-style-card h-auto flex-col items-start rounded-xl border p-3 text-left disabled:opacity-50 ${appearance?.style === style.id ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : ''}`}
              onClick={() =>
                onPreviewAppearance({
                  ...appearance,
                  style: style.id,
                  ...(style.id === 'signature' ? { palette } : {}),
                })
              }
            >
              <span className="mb-3 flex gap-1.5">
                {style.colors.map((color) => (
                  <span
                    key={color}
                    className="size-5 rounded-full border border-border"
                    style={{ background: color }}
                  />
                ))}
              </span>
              <span className="text-xs font-medium">{style.name}</span>
            </Button>
          ))}
          <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
            <span className="mr-2 text-xs text-muted-foreground">Spacing</span>
            {(['Airy', 'Balanced', 'Compact'] as const).map((spacing) => (
              <Button
                key={spacing}
                size="sm"
                variant="outline"
                disabled={disabled}
                aria-pressed={appearance?.spacing === spacing}
                className={
                  appearance?.spacing === spacing
                    ? 'border-primary bg-primary/5'
                    : ''
                }
                onClick={() => onPreviewAppearance({ ...appearance, spacing })}
              >
                {spacing}
              </Button>
            ))}
          </div>
          {appearance && (
            <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
              <Button
                size="sm"
                disabled={disabled}
                onClick={() => onApply(appearancePrompt(appearance))}
              >
                Save style
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => onPreviewAppearance(null)}
              >
                Reset preview
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
          <div className="space-y-1.5">
            <label
              htmlFor="site-element"
              className="text-xs font-medium text-muted-foreground"
            >
              Choose a detail
            </label>
            <Select
              value={targetKey}
              disabled={disabled || !targets.length}
              onValueChange={(key) => {
                const selected = targets.find((item) => item.key === key);
                setTargetKey(key);
                setValue(selected?.value || '');
              }}
            >
              <SelectTrigger
                id="site-element"
                aria-label="Choose a detail"
                className="h-10 w-full bg-background"
              >
                <SelectValue
                  placeholder={
                    targets.length
                      ? 'Select from your website…'
                      : 'No editable elements yet'
                  }
                />
              </SelectTrigger>
              <SelectContent align="start" className="max-w-[calc(100vw-24px)]">
                {targets.map((item) => (
                  <SelectItem key={item.key} value={item.key}>
                    <span className="truncate">{item.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="site-detail-value"
              className="text-xs font-medium text-muted-foreground"
            >
              Your new text
            </label>
            <Textarea
              id="site-detail-value"
              value={value}
              maxLength={3000}
              disabled={!target || disabled}
              rows={2}
              onChange={(event) => setValue(event.target.value)}
            />
          </div>
          <Button
            className="self-end"
            disabled={disabled || !target || !value.trim()}
            onClick={apply}
          >
            <WandSparkles className="size-4" />
            Apply change
          </Button>
        </div>
      )}
    </section>
  );
}
